import { Icon, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { ImagePreviewTrigger } from "@/features/identity-hub/components/dashboard/ImagePreviewTrigger";
import { PersonaPortrait } from "@/features/personas/components/PersonaPortrait/PersonaPortrait";
import { PersonaStaleBadge } from "@/features/personas/components/PersonaStaleBadge/PersonaStaleBadge";
import {
  humanizePersonaSentence,
  humanizePersonaTitle,
} from "@/features/personas/lib/persona-format";

import { PersonaOpportunityAngleCountBadge } from "../../shared/PersonaOpportunityPersonaGroupHeader/PersonaOpportunityAngleCountBadge.js";
import { ExpandableCellText } from "../../table/PersonaOpportunityTable/persona-opportunity-cells.js";

import { isNoPersonaGroup, type OpportunityHubGroup } from "./hub-chrome-grouping.js";
import type { HubSetsById } from "./hub-chrome.types.js";
import { OpportunityHubSetBadge } from "./OpportunityHubSetBadge.js";

/**
 * Persona group header. Expanded, it is the top of a card the group's table and
 * footer continue; collapsed, it stands alone.
 */
export function OpportunityHubGroupFold({
  group,
  expanded,
  onToggle,
  selectedCount = 0,
  showSetBadge = false,
  setsById,
}: {
  group: OpportunityHubGroup;
  expanded: boolean;
  onToggle: () => void;
  /** Selected rows in this group, shown beside the angle count. */
  selectedCount?: number;
  /** Names the set on the header when more than one set is selected. */
  showSetBadge?: boolean;
  setsById?: HubSetsById;
}) {
  const { t } = useTranslation();
  const isNoPersona = isNoPersonaGroup(group);
  const title = isNoPersona
    ? t("personaOpportunities.hub.groupBy.noPersona", "No persona")
    : humanizePersonaTitle(group.title);
  const who = isNoPersona
    ? t(
        "personaOpportunities.hub.groupBy.noPersonaSubtitle",
        "Opportunities that are not tied to a persona.",
      )
    : group.who
      ? humanizePersonaSentence(group.who)
      : null;
  const avatarUrl = isNoPersona ? null : group.avatarUrl;
  const personalityKey = isNoPersona ? "no-persona" : group.personalityKey || group.key;
  const pains = isNoPersona ? [] : toSentences(group.pains);
  const motivations = isNoPersona ? [] : toSentences(group.motivations);
  const hasDrivers = pains.length > 0 || motivations.length > 0;
  const count = group.rollup?.count ?? group.rows.length;

  return (
    <div
      className={cn(
        "relative border border-border bg-card",
        expanded ? "rounded-t-md" : "rounded-md",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(
          "flex w-full gap-3 px-4 py-3.5 text-left transition-colors duration-150 ease-out hover:bg-muted/40 motion-reduce:transition-none",
          hasDrivers ? "items-start" : "items-center",
          expanded ? "rounded-t-md" : "rounded-md",
        )}
      >
        <Icon
          name="chevron-right"
          size="sm"
          className={cn(
            "shrink-0 text-muted-foreground transition-transform duration-150 ease-out motion-reduce:transition-none",
            hasDrivers && "mt-0.5",
            expanded && "rotate-90",
          )}
          aria-hidden
        />
        <span aria-hidden className="size-9 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold leading-5 tracking-[-0.01em] text-foreground">
            {title}
          </p>
          {who && expanded ? (
            <p className="mt-0.5 max-w-3xl text-xs leading-[18px] text-muted-foreground">{who}</p>
          ) : null}
          {who && !expanded ? (
            <ExpandableCellText
              text={who}
              className="mt-0.5 block truncate text-xs text-muted-foreground"
            />
          ) : null}
          {hasDrivers ? (
            <PersonaDrivers pains={pains} motivations={motivations} expanded={expanded} />
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {showSetBadge && group.setName ? (
            <OpportunityHubSetBadge
              name={group.setName}
              set={group.setId ? setsById?.get(group.setId) : null}
            />
          ) : null}
          <PersonaOpportunityAngleCountBadge
            title={title}
            angleCount={count}
            angles={group.rollup ? [] : group.rows.map((row) => row.angle)}
            stats={group.rollup ?? null}
            selectedCount={selectedCount}
          />
          {group.isStale ? <PersonaStaleBadge /> : null}
        </div>
      </button>
      <div
        className={cn(
          "absolute left-11",
          hasDrivers ? "top-3.5" : "top-1/2 -translate-y-1/2",
          !avatarUrl && "pointer-events-none",
        )}
      >
        <ImagePreviewTrigger src={avatarUrl} alt={title} className="group/avatar rounded-full">
          <span className="relative block transition-transform duration-150 ease-out motion-safe:group-hover/avatar:scale-105 motion-reduce:transition-none">
            <PersonaPortrait
              avatarUrl={avatarUrl}
              personalityKey={personalityKey}
              title={title}
              className="size-9 ring-1 ring-border transition-shadow duration-150 ease-out group-hover/avatar:ring-primary/50 motion-reduce:transition-none"
            />
            {avatarUrl ? (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-foreground/50 opacity-0 transition-opacity duration-150 ease-out group-hover/avatar:opacity-100 motion-reduce:transition-none"
              >
                <Icon name="maximize-2" size="xs" color="inherit" className="text-background" />
              </span>
            ) : null}
          </span>
        </ImagePreviewTrigger>
      </div>
    </div>
  );
}

type PersonaDriverKind = "pain" | "motivation";

const DRIVER_TONE: Record<PersonaDriverKind, { dot: string; label: string }> = {
  pain: { dot: "bg-orange", label: "text-orange-subtle-foreground" },
  motivation: { dot: "bg-lime", label: "text-lime-subtle-foreground" },
};

function toSentences(values: readonly string[] | undefined): string[] {
  return (values ?? []).map(humanizePersonaSentence).filter((value) => value !== "");
}

/** Collapsed: the lead pain point and motivation; expanded: every one of each. */
function PersonaDrivers({
  pains,
  motivations,
  expanded,
}: {
  pains: string[];
  motivations: string[];
  expanded: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "mt-2 grid gap-x-6 gap-y-2.5 md:grid-cols-[18rem_minmax(0,1fr)]",
        expanded && "mt-3 border-t border-border-subtle pt-3",
      )}
    >
      {pains.length > 0 ? (
        <PersonaDriver
          kind="pain"
          label={
            expanded
              ? t("personaOpportunities.active.painPoints", "Pain points")
              : t("personaOpportunities.active.painPoint", "Pain point")
          }
          items={pains}
          expanded={expanded}
        />
      ) : null}
      {motivations.length > 0 ? (
        <PersonaDriver
          kind="motivation"
          label={
            expanded
              ? t("personaOpportunities.active.motivations", "Motivations")
              : t("personaOpportunities.active.motivation", "Motivation")
          }
          items={motivations}
          expanded={expanded}
        />
      ) : null}
    </div>
  );
}

function PersonaDriver({
  kind,
  label,
  items,
  expanded,
}: {
  kind: PersonaDriverKind;
  label: string;
  items: string[];
  expanded: boolean;
}) {
  const { t } = useTranslation();
  const tone = DRIVER_TONE[kind];
  const hiddenCount = items.length - 1;

  return (
    <div className={cn("min-w-0", kind === "motivation" && "md:col-start-2")}>
      <p className="flex items-center gap-1.5">
        <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", tone.dot)} />
        <span className={cn("text-[10px] font-semibold uppercase tracking-wide", tone.label)}>
          {label}
        </span>
        {expanded || hiddenCount === 0 ? null : (
          <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
            {t("personaOpportunities.hub.groupBy.moreDrivers", {
              count: hiddenCount,
              defaultValue_one: "+{{count}} more",
              defaultValue_other: "+{{count}} more",
            })}
          </span>
        )}
      </p>
      {expanded ? (
        <span className="mt-1.5 flex flex-col gap-1.5">
          {items.map((item, index) => (
            <span
              key={`${kind}-${index}`}
              className="flex gap-2 text-[12.5px] leading-[18px] text-foreground"
            >
              <span aria-hidden className="text-muted-foreground">
                ·
              </span>
              <span>{item}</span>
            </span>
          ))}
        </span>
      ) : (
        <ExpandableCellText
          text={items[0] ?? ""}
          className="mt-0.5 line-clamp-2 text-[12.5px] leading-[18px] text-foreground"
        />
      )}
    </div>
  );
}

